import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { colors } from "../../../Shared/colors";
import {
  useGetSingleHierarchyQuery,
  useGetActiveStaffQuery,
  useLazyGetStaffKpiQuery,
} from "../../../features/employeeSlice";
import { useGetAllProjectsQuery } from "../../../features/projectSlice";
import { useGetClientsForDropdownQuery } from "../../../features/clientSlice";
import {
  useLazySearchLeadsFilterQuery,
  useLazySearchLeadsQuery,
} from "../../../features/leadsSlice";
import { SearchLeadsDataTable } from "./SearchLeadsDataTable";
import { toast, Toaster } from "react-hot-toast";
import { Box } from "@mui/material";
import { decryptData } from "../../../services/crypto";
import { usePaginationFromURL } from "../../../hooks/usePaginationFromURL";

export const SearchLeadsFirst = () => {
  let staffName = null;
  let storedStaffName = localStorage.getItem("staffName");

  if (storedStaffName) {
    try {
      staffName = decryptData(storedStaffName);
    } catch (error) {
        // Failed to decrypt data
    }
  }

  let staffId = null;
  let storedStaffId = localStorage.getItem("id");

  if (storedStaffId) {
    try {
      staffId = decryptData(storedStaffId);
    } catch (error) {
        // Failed to decrypt data
    }
  }

  let role = null;
    let storedStaffRole = localStorage.getItem("role");
  
    if (storedStaffRole) {
      try {
        role = decryptData(storedStaffRole);
      } catch (error) {
        // Failed to decrypt data
      }
    }
  
  // Use URL-based pagination hook
  const { currentPage, pageSize, setPage: setCurrentPage, setPageSize: setpageSize } = usePaginationFromURL({
    defaultPage: 1,
    defaultPageSize: 50
  });
  const [isNewChecked, setIsNewChecked] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [kpiData, setkpiData] = useState({
    start: "",
    end: "",
    clientID: { id: "", label: "select" },
    callStatus: "",
    leadID: "",
    employeeID: null, // Start with null - "Select Staff Member" position
    project: "",
    phoneNumber: "",
    isNew: false,
  });
  const [searchFilters, setSearchFilters] = useState({
    start: "",
    end: "",
    clientID: { id: "", label: "select" },
    callStatus: "",
    leadID: "",
    employeeID: { id: staffId, label: staffName },
    project: "",
    phoneNumber: "",
  });

  const { data: hierarchyStaff } = useGetSingleHierarchyQuery({ staffId });
  const { data: staff } = useGetActiveStaffQuery();
  
  // Determine which employee ID to use for client dropdown
  // If staff member is explicitly selected, use that; otherwise pass undefined (backend will use logged-in user)
  const employeeIdForClients = (kpiData?.employeeID?.id && kpiData.employeeID.id !== "") 
    ? kpiData.employeeID.id 
    : undefined; // Pass undefined - backend will default to logged-in user
  
  const { data: clients, isFetching: isClientFetcing, isLoading: isClientLoading } = useGetClientsForDropdownQuery(employeeIdForClients);

  const handleCheckboxChange = (event) => {
    setIsNewChecked(event.target.checked);
    handleSearch();
  };

  const [trigger, { data: leadData, isFetching, error }] =
    useLazySearchLeadsFilterQuery();

  const { data: project } = useGetAllProjectsQuery();

  const staffOptions = useMemo(() => {
    let options = [{ id: "", label: "Select Staff Member" }];
    hierarchyStaff?.map((staff) =>
      options.push({
        id: staff?._id,
        label: staff?.firstName + " " + staff?.lastName,
      })
    );

    return options;
  }, [hierarchyStaff]);

  const allStaffOptions = useMemo(() => {
    let options = [{ id: "", label: "Select Staff Member" }];
    staff?.map((staff) =>
      options.push({
        id: staff?._id,
        label: staff?.firstName + " " + staff?.lastName,
      })
    );

    return options;
  }, [staff]);

  const clientOptions = useMemo(() => {
    let options = [{ id: "", label: "select" }];
    // New dropdown endpoint returns clients in format: [{ id, label }]
    if (clients && Array.isArray(clients)) {
      clients.forEach((client) => {
        options.push({
          id: client.id,
          label: client.label,
        });
      });
    }
    return options;
  }, [clients]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setkpiData({
      ...kpiData,
      [name]: value,
    });
  };

  const handleSearch = () => {
    const {
      project,
      classification,
      source,
      numTasks,
      leadID,
      from,
      start,
      end,
      callStatus,
      clientID,
      phoneNumber,
    } = kpiData;

    let employeeId =
      role === "ceo" || role === "admin" || role === "sbdm" || role === "bdm" || role === "account"
        ? kpiData.employeeID?.id || staffId
        : kpiData.employeeID?.id;

    const payload = {
      start,
      end,
      callStatus,
      projectID: project,
      employeeID: employeeId,
      clientID: clientID?.id,
      from,
      classification,
      phoneNumber,
      isNew: isNewChecked,
      source,
      numTasks,
      leadID,
      page: currentPage,
      pageSize: pageSize,
    };

    trigger(payload)
      .then((res) => {
        if (!isInitialLoad) {
        toast.success("Leads loaded successfully!");
        }
      })
      .catch((err) => {
        // Error occurred
        const errorMessage =
          err?.data?.error?.message || "Failed to load leads";
        toast.error(errorMessage);
      })
      .finally(() => {
        setkpiData({
          ...kpiData,
          classification: "",
          source: "",
          numTasks: "",
          leadID: "",
          callStatus: "",
          start: "",
          end: "",
          project: "",
          from: "",
        });
      });

    setSearchFilters({
      projectID: project,
      employeeID: employeeId || staffId,
      classification,
      source,
      start: "",
      end: "",
      numTasks,
      leadID,
      callStatus,
      from,
    });
  };

  // Initial search when component mounts
  useEffect(() => {
    if (isInitialLoad) {
      const {
        projectID,
        classification,
        source,
        numTasks,
        leadID,
        from,
        employeeID,
        start,
        end,
        callStatus,
        phoneNumber,
      } = searchFilters;

      let employeeId = kpiData.employeeID?.id || staffId;

      if (
        role === "ceo" ||
        role === "admin" ||
        role === "sbdm" ||
        role === "account" ||
        role === "bdm"
      ) {
        employeeId = kpiData.employeeID?.id || staffId;
      }

      trigger({
        projectID,
        employeeID: employeeId,
        from,
        classification,
        start,
        end,
        source,
        numTasks,
        isNew: isNewChecked,
        callStatus,
        phoneNumber,
        leadID,
        page: currentPage,
        pageSize: pageSize,
      }).then(() => {
        setIsInitialLoad(false);
      });
    }
  }, [isInitialLoad]);

  // Handle page changes and new leads checkbox changes
  useEffect(() => {
    if (!isInitialLoad) {
      const {
        projectID,
        classification,
        source,
        numTasks,
        leadID,
        from,
        employeeID,
        start,
        end,
        callStatus,
        phoneNumber,
      } = searchFilters;

      let employeeId = kpiData.employeeID?.id || staffId;

      if (
        role === "ceo" ||
        role === "admin" ||
        role === "sbdm" ||
        role === "account" ||
        role === "bdm"
      ) {
        employeeId = kpiData.employeeID?.id || staffId;
      }

      trigger({
        projectID,
        employeeID: employeeId,
        from,
        classification,
        start,
        end,
        source,
        numTasks,
        isNew: isNewChecked,
        callStatus,
        phoneNumber,
        leadID,
        page: currentPage,
        pageSize: pageSize,
      });
    }
  }, [currentPage, isNewChecked, isInitialLoad]);

  // Handle page size changes - reset to page 1 and trigger API call
  useEffect(() => {
    if (!isInitialLoad) {
      if (currentPage > 1) {
        setCurrentPage(1);
      } else {
        // If already on page 1, trigger API call directly
        const {
          projectID,
          classification,
          source,
          numTasks,
          leadID,
          from,
          employeeID,
          start,
          end,
          callStatus,
          phoneNumber,
        } = searchFilters;

        let employeeId = kpiData.employeeID?.id || staffId;

        if (
          role === "ceo" ||
          role === "admin" ||
          role === "sbdm" ||
          role === "account" ||
          role === "bdm"
        ) {
          employeeId = kpiData.employeeID?.id || staffId;
        }

        trigger({
          projectID,
          employeeID: employeeId,
          from,
          classification,
          start,
          end,
          source,
          numTasks,
          isNew: isNewChecked,
          callStatus,
          phoneNumber,
          leadID,
          page: 1,
          pageSize: pageSize,
        });
      }
    }
  }, [pageSize, isInitialLoad]);

  return (
    <>
      <Main>
        <SearchContainer>
          <SearchHeader>
            <SearchTitle>
              <SearchIcon>🔍</SearchIcon>
              Search Leads
            </SearchTitle>
          </SearchHeader>
          
          <SearchForm>
            <FormRow>
              <FormGroup>
                <FormLabel>Start Date</FormLabel>
                <DateInput
                  type="date"
                  name="start"
                  id="start"
                  value={kpiData.start}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>End Date</FormLabel>
                <DateInput
                  type="date"
                  name="end"
                  id="end"
                  value={kpiData.end}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Phone Number</FormLabel>
                <TextInput
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={kpiData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Project</FormLabel>
                <SelectInput
                  name="project"
                  id="project"
                  value={kpiData.project}
                  onChange={handleChange}
                >
                  <option value="">Select Project</option>
                  {project?.map((proj) => (
                    <option key={proj._id} value={proj._id}>{proj.projectName}</option>
                  ))}
                </SelectInput>
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Lead ID</FormLabel>
                <TextInput
                  type="text"
                  name="leadID"
                  id="lead"
                  value={kpiData.leadID}
                  onChange={handleChange}
                  placeholder="Enter Lead ID"
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel>Call Status</FormLabel>
                <SelectInput
                  name="callStatus"
                  id="callStatus"
                  value={kpiData.callStatus}
                  onChange={handleChange}
                >
                  <option value="">Select Call Status</option>
                  <option value="Whatsapp">WhatsApp</option>
                  <option value="Call connected">Call Connected</option>
                  <option value="Call Not Connected">Call Not Connected</option>
                </SelectInput>
              </FormGroup>
              
              {role === "ceo" ||
                role === "admin" ||
                role === "sbdm" ||
                role === "account" ||
                role === "bdm" ? (
                <FormGroup>
                  <FormLabel>Staff Member</FormLabel>
                  <AutocompleteContainer>
                    <Autocomplete
                      id="staff-autocomplete"
                      size="small"
                      options={
                        role === "ceo" || role === "admin" || role === "account"
                          ? allStaffOptions
                          : staffOptions
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(e, value) => {
                        // If value is null or "Select Staff Member" (empty id), set to null
                        // Otherwise, use the selected staff member
                        const newEmployeeID = (value && value.id && value.id !== "") 
                          ? value 
                          : null; // Set to null for "Select Staff Member"
                        
                        // Reset client selection when staff changes
                        setkpiData({ 
                          ...kpiData, 
                          employeeID: newEmployeeID,
                          clientID: { id: "", label: "select" }
                        });
                      }}
                      value={kpiData.employeeID || null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Staff Member"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: colors.white,
                              '& fieldset': {
                                borderColor: colors.inputBorderWhite,
                                borderWidth: '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.brown,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: colors.brown,
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '8px 12px',
                              fontSize: '13px',
                            },
                          }}
                        />
                      )}
                    />
                  </AutocompleteContainer>
                </FormGroup>
              ) : null}

              <FormGroup>
                <FormLabel>Client</FormLabel>
                <AutocompleteContainer>
                  <Autocomplete
                    id="client-autocomplete"
                    size="small"
                    disabled={isClientLoading || isClientFetcing}
                    options={clientOptions}
                    getOptionLabel={(option) => option.label}
                    onChange={(e, value) => {
                      setkpiData({ ...kpiData, clientID: value });
                    }}
                    value={kpiData.clientID || null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Client"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: colors.white,
                            '& fieldset': {
                              borderColor: colors.inputBorderWhite,
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: colors.brown,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.brown,
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            padding: '8px 12px',
                            fontSize: '13px',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.label}
                      </Box>
                    )}
                  />
                </AutocompleteContainer>
              </FormGroup>
            </FormRow>
            
            <ActionRow>
              <SearchButton onClick={handleSearch} disabled={isFetching}>
                {isFetching ? (
                  <>
                    <Spinner />
                    Searching...
                  </>
                ) : (
                  <>
                    <SearchIcon>🔍</SearchIcon>
                    Search Leads
                  </>
                )}
              </SearchButton>
            </ActionRow>
          </SearchForm>
        </SearchContainer>
      </Main>
      <Toaster />
      <SearchLeadsDataTable
        leads={leadData}
        pageSize={pageSize}
        setPageSize={setpageSize}
        setPage={setCurrentPage}
        checked={isNewChecked}
        handleCheckboxChange={handleCheckboxChange}
        isFetching={isFetching}
        currentPage={currentPage}
        totalPages={Math.ceil(leadData?.totalLeads / pageSize)}
      />
    </>
  );
};

const Main = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem;
`;

const SearchContainer = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.brown}, ${colors.textBlue});
    z-index: 1;
  }
`;

const SearchHeader = styled.div`
  padding: 1rem 1.25rem 0.5rem 1.25rem;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, ${colors.white} 100%);
`;

const SearchTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.text};
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
`;

const SearchIcon = styled.span`
  font-size: 1.25rem;
  filter: grayscale(0.2);
`;

const SearchForm = styled.div`
  padding: 1rem 1.25rem 1.25rem 1.25rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const FormLabel = styled.label`
  color: ${colors.text};
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.1rem;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TextInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.text};
  font-size: 0.8rem;
  transition: all 0.2s ease;
  outline: none;
  height: 36px;
  
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
  }
  
  &::placeholder {
    color: ${colors.inputFeild};
    font-size: 0.75rem;
  }
  
  &:hover {
    border-color: ${colors.brown};
  }
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.text};
  font-size: 0.8rem;
  transition: all 0.2s ease;
  outline: none;
  height: 36px;
  
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
  }
  
  &:hover {
    border-color: ${colors.brown};
  }
`;

const SelectInput = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.text};
  font-size: 0.8rem;
  transition: all 0.2s ease;
  outline: none;
  cursor: pointer;
  height: 36px;
  
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
  }
  
  &:hover {
    border-color: ${colors.brown};
  }
  
  option {
    padding: 6px;
    font-size: 0.8rem;
  }
`;

const AutocompleteContainer = styled.div`
  width: 100%;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${colors.inputBorderWhite};
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 20px;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(36, 147, 147, 0.3);
  min-width: 140px;
  justify-content: center;
  height: 36px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(36, 147, 147, 0.4);
    background: linear-gradient(135deg, #1a6b6b 0%, ${colors.brown} 100%);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid ${colors.white};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
